/*
*
 * Central registry for every exam supported by FullPracticeTests.
 *
 * There are two tiers:
 *  - "Custom" exams (toefl, sat, act, ielts, gre) with bespoke DB schemas,
 *    scoring code, exam runners, and report pages.
 *  - "Generic" exams — every other entry — which share a common data shape,
 *    a generic MCQ exam runner, generic scoring, and generic resource pages.
 *
 * `available: true` means the exam is fully wired end-to-end and visible
 * on the exam chooser. `mediaStatus` signals whether the full content is
 * in place or the listening/image portions are running on placeholders.
 */

import { type LucideIcon, Ambulance, BarChart3, Bird, Book, BookMarked, BookOpen, BookText, ClipboardList, Dna, Dumbbell, Earth, Globe, GraduationCap, HandHeart, HeartPulse, Home, Hospital, Languages, Laptop, Medal, Monitor, Pill, Plane, Ruler, Scale, Scissors, Shield, Smile, Stethoscope, Syringe, TrendingUp, Truck, Utensils } from "lucide-react";

export type ExamType =
  // Custom (tier-1) exams
  | "toefl" | "sat" | "ielts" | "gre" | "act"
  // Academic admissions (generic)
  | "gmat" | "psat" | "lsat"
  // Medical licensing (generic)
  | "nclex" | "usmle" | "usmle-step2" | "pance" | "mcat" | "inbde" | "ap"
  // Professional certifications (generic)
  | "cfa" | "sie" | "pmp" | "comptia" | "asvab" | "servsafe"
  // English proficiency (generic)
  | "cambridge-c1" | "duolingo-english" | "oet" | "pte"
  // Licensing & vocational (generic)
  | "cdl" | "real-estate" | "insurance-license" | "cosmetology"
  | "faa-part-107" | "cna" | "nremt" | "nasm-cpt"
  // Foreign languages (generic)
  | "hsk" | "jlpt" | "topik" | "dele" | "delf" | "goethe-c1" | "fsp";

export type ExamCategory =
  | "academic" | "english" | "medical" | "professional" | "licensing" | "language";

export type MediaStatus =
  /** Content fully available including audio + images. */
  | "complete"
  /** Text and questions ready; audio/images render as placeholders. */
  | "text-only"
  /** Content generated on a placeholder basis (for exams with no seeds). */
  | "placeholder";

/**
 * Public-facing readiness tier per the 2026-05-20 readiness evaluation
 * (see `docs/exam-readiness-2026-05-20.md`).
 *
 * - `live` — fully ship-ready, passes all 6 framework axes (depth, variety,
 *   structure, scoring, media, gen difficulty). Shown without any badge.
 * - `beta` — works end-to-end and is useful, but missing one polish layer
 *   (TTS audio, custom report scale, etc.) — shown with a "Beta" pill so
 *   users know it's not the final form.
 * - `coming_soon` — landing page renders for SEO + lead capture, but the
 *   exam-start button is replaced with "Coming Soon" copy. Used for exams
 *   that need domain-expert review (medical boards, finance, formal logic)
 *   or have production-broken content.
 * - `hidden` — not in the catalog. Reserved; no exam currently uses this.
 *
 * The legacy `available: boolean` field is **derived from status**:
 *   - `live` / `beta`  → `available: true`
 *   - `coming_soon` / `hidden` → `available: false`
 * Existing UI code paths that check `available` continue to work; status
 * adds the new `beta` badge + the coming-soon distinction.
 */
export type ExamStatus = "live" | "beta" | "coming_soon" | "hidden";

export interface SectionConfig {
  id: string;
  label: string;
  duration: number; // minutes
  scoreMax: number;
  /** Section renders a placeholder (e.g. listening without audio). */
  placeholder?: boolean;
}

export interface ExamConfig {
  name: string;
  slug: ExamType;
  color: string;             // tailwind color name
  description: string;
  category: ExamCategory;
  /** Tier: "custom" uses bespoke pages; "generic" uses the shared runner. */
  tier: "custom" | "generic";
  v1ScoreMax: number;        // score without placeholder sections
  v2ScoreMax: number;        // full score incl. placeholder sections
  /** Readiness tier — see ExamStatus for semantics. */
  status: ExamStatus;
  /**
   * Derived from `status`: true for `live`/`beta`, false for `coming_soon`/
   * `hidden`. Kept as a separate field for backward compatibility with the
   * UI code paths that already gate on it.
   */
  available: boolean;
  mediaStatus: MediaStatus;
  scoreLabel: string;        // display suffix e.g. "/120"
  icon: LucideIcon;          // lucide icon component (rendered by the hub, dashboard, report hero, sidebars)
  sections: SectionConfig[];
}

export const EXAM_CONFIGS: Record<ExamType, ExamConfig> = {
  // ─────────────────────────────────────── TIER 1: CUSTOM ──────────────────
  toefl: {
    name: "TOEFL iBT",
    slug: "toefl",
    color: "blue",
    category: "english",
    tier: "custom",
    description: "Internet-Based Test — the world's most accepted English proficiency exam",
    v1ScoreMax: 120,
    v2ScoreMax: 120,
    status: "live",
    available: true,
    mediaStatus: "complete",
    scoreLabel: "/120",
    icon: Globe,
    sections: [
      { id: "reading",   label: "Reading",   duration: 35, scoreMax: 30 },
      { id: "listening", label: "Listening", duration: 36, scoreMax: 30 },
      { id: "speaking",  label: "Speaking",  duration: 16, scoreMax: 30 },
      { id: "writing",   label: "Writing",   duration: 29, scoreMax: 30 },
    ],
  },
  sat: {
    name: "SAT",
    slug: "sat",
    color: "indigo",
    category: "academic",
    tier: "custom",
    description: "College Board — Reading & Writing and Math sections",
    v1ScoreMax: 1600,
    v2ScoreMax: 1600,
    status: "live",
    available: true,
    mediaStatus: "text-only",
    scoreLabel: "/1600",
    icon: Ruler,
    sections: [
      { id: "rw",   label: "Reading & Writing", duration: 64, scoreMax: 800 },
      { id: "math", label: "Math",              duration: 70, scoreMax: 800 },
    ],
  },
  ielts: {
    name: "IELTS Academic",
    slug: "ielts",
    color: "green",
    category: "english",
    tier: "custom",
    description: "International English Language Testing System — Academic module",
    v1ScoreMax: 9,
    v2ScoreMax: 9,
    status: "live",
    available: true,
    mediaStatus: "text-only",
    scoreLabel: "/9.0",
    icon: Earth,
    sections: [
      { id: "listening", label: "Listening", duration: 30, scoreMax: 9 },
      { id: "reading",   label: "Reading",   duration: 60, scoreMax: 9 },
      { id: "writing",   label: "Writing",   duration: 60, scoreMax: 9 },
      { id: "speaking",  label: "Speaking",  duration: 14, scoreMax: 9 },
    ],
  },
  gre: {
    name: "GRE General",
    slug: "gre",
    color: "purple",
    category: "academic",
    tier: "custom",
    description: "Graduate Record Examination — Verbal, Quantitative, Analytical Writing",
    v1ScoreMax: 340,
    v2ScoreMax: 340,
    status: "live",
    available: true,
    mediaStatus: "text-only",
    scoreLabel: "/340",
    icon: BookOpen,
    sections: [
      { id: "verbal", label: "Verbal Reasoning",      duration: 41, scoreMax: 170 },
      { id: "quant",  label: "Quantitative Reasoning", duration: 47, scoreMax: 170 },
      { id: "aw",     label: "Analytical Writing",     duration: 30, scoreMax: 6 },
    ],
  },
  act: {
    name: "ACT",
    slug: "act",
    color: "orange",
    category: "academic",
    tier: "custom",
    description: "American College Testing — English, Math, Reading, and Science",
    v1ScoreMax: 36,
    v2ScoreMax: 36,
    status: "live",
    available: true,
    mediaStatus: "text-only",
    scoreLabel: "/36",
    icon: BookMarked,
    sections: [
      { id: "english", label: "English", duration: 45, scoreMax: 36 },
      { id: "math",    label: "Math",    duration: 60, scoreMax: 36 },
      { id: "reading", label: "Reading", duration: 35, scoreMax: 36 },
      { id: "science", label: "Science", duration: 35, scoreMax: 36 },
    ],
  },

  // ─────────────────────────────────────── TIER 2: GENERIC ─────────────────
  // Academic admissions
  gmat: {
    name: "GMAT", slug: "gmat", color: "indigo", category: "academic", tier: "generic",
    description: "Graduate Management Admission Test — for MBA and business school",
    // Promoted to live 2026-07-19: quant template-slots + geometry framing replaced
    // (18 items), judge 21% flagged / 83% realistic (best on platform), zero
    // reuse, promotion E2E 64q → 805/805. Adaptivity remains approximated.
    v1ScoreMax: 805, v2ScoreMax: 805, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "/805", icon: BookText,
    sections: [
      { id: "quant",  label: "Quantitative Reasoning", duration: 45, scoreMax: 90 },
      { id: "verbal", label: "Verbal Reasoning",       duration: 45, scoreMax: 90 },
      { id: "di",     label: "Data Insights",          duration: 45, scoreMax: 90 },
    ],
  },
  psat: {
    name: "PSAT", slug: "psat", color: "blue", category: "academic", tier: "generic",
    description: "Preliminary SAT — SAT practice and National Merit qualifier",
    // Promoted to live 2026-07-19 after three blueprint/judge rounds:
    // Advanced-Math coverage 1%→19% vs 16% target (72 verified swaps); 52
    // passage/stem mismatches fixed; all 228 comprehension items rebuilt to
    // the text-anchored distractor standard. Judge 13% flagged / 83%
    // realistic (platform best); promotion E2E 98/98 → 1520/1520.
    v1ScoreMax: 1520, v2ScoreMax: 1520, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "/1520", icon: Book,
    sections: [
      { id: "rw",   label: "Reading & Writing", duration: 64, scoreMax: 760 },
      { id: "math", label: "Math",              duration: 70, scoreMax: 760 },
    ],
  },
  lsat: {
    name: "LSAT", slug: "lsat", color: "purple", category: "academic", tier: "generic",
    description: "Law School Admission Test — logical reasoning and reading comprehension",
    v1ScoreMax: 180, v2ScoreMax: 180, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "/180", icon: Scale,
    // Analytical Reasoning (Logic Games) was retired by LSAC in August 2024.
    // Current LSAT has two LR sections + Reading Comprehension only.
    sections: [
      { id: "lr1", label: "Logical Reasoning I",   duration: 35, scoreMax: 26 },
      { id: "lr2", label: "Logical Reasoning II",  duration: 35, scoreMax: 26 },
      { id: "rc",  label: "Reading Comprehension", duration: 35, scoreMax: 27 },
    ],
  },

  // Medical licensing
  nclex: {
    name: "NCLEX-RN", slug: "nclex", color: "green", category: "medical", tier: "generic",
    description: "National Council Licensure Examination for registered nurses",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "%", icon: Hospital,
    sections: [
      { id: "nclex", label: "NCLEX-RN", duration: 300, scoreMax: 100 },
    ],
  },
  usmle: {
    name: "USMLE Step 1", slug: "usmle", color: "red", category: "medical", tier: "generic",
    description: "United States Medical Licensing Examination — Step 1 basic sciences",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "%", icon: Syringe,
    sections: [
      { id: "usmle", label: "USMLE Step 1", duration: 480, scoreMax: 100 },
    ],
  },
  "usmle-step2": {
    name: "USMLE Step 2 CK", slug: "usmle-step2", color: "red", category: "medical", tier: "generic",
    description: "United States Medical Licensing Examination — Step 2 Clinical Knowledge",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "%", icon: Pill,
    sections: [
      { id: "usmle-step2", label: "USMLE Step 2 CK", duration: 480, scoreMax: 100 },
    ],
  },
  ap: {
    name: "AP Exams", slug: "ap", color: "blue", category: "academic", tier: "generic",
    description: "Advanced Placement exams — college-level subject tests",
    // Flipped to beta 2026-07-15: answer-key audit 40/40 clean, content
    // integrity clean. FRQ sections render but are not auto-scored (see
    // BETA_NOTES) — the score reflects the MCQ section.
    v1ScoreMax: 5, v2ScoreMax: 5, status: "beta", available: true, mediaStatus: "text-only",
    scoreLabel: "/5", icon: GraduationCap,
    sections: [
      { id: "mcq", label: "Multiple Choice", duration: 105, scoreMax: 5 },
      { id: "frq", label: "Free Response", duration: 90, scoreMax: 5 },
    ],
  },
  pance: {
    name: "PANCE", slug: "pance", color: "teal", category: "medical", tier: "generic",
    description: "Physician Assistant National Certifying Examination",
    v1ScoreMax: 800, v2ScoreMax: 800, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "/800", icon: Stethoscope,
    sections: [
      { id: "pance", label: "PANCE", duration: 300, scoreMax: 800 },
    ],
  },
  mcat: {
    name: "MCAT", slug: "mcat", color: "red", category: "medical", tier: "generic",
    description: "Medical College Admission Test — for US and Canada medical schools",
    v1ScoreMax: 528, v2ScoreMax: 528, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "/528", icon: Dna,
    sections: [
      { id: "cpbs",  label: "Chemical & Physical Sciences", duration: 95, scoreMax: 132 },
      { id: "cars",  label: "Critical Analysis & Reasoning", duration: 90, scoreMax: 132 },
      { id: "bbls",  label: "Biological & Biochemical Sciences", duration: 95, scoreMax: 132 },
      { id: "psbs",  label: "Psychological, Social & Biological Foundations", duration: 95, scoreMax: 132 },
    ],
  },
  inbde: {
    name: "INBDE", slug: "inbde", color: "cyan", category: "medical", tier: "generic",
    description: "Integrated National Board Dental Examination",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "coming_soon", available: false, mediaStatus: "complete",
    scoreLabel: "%", icon: Smile,
    sections: [
      { id: "biomedical_1", label: "Biomedical — Set 1", duration: 60, scoreMax: 12 },
      { id: "biomedical_2", label: "Biomedical — Set 2", duration: 60, scoreMax: 13 },
      { id: "biomedical_3", label: "Biomedical — Set 3", duration: 60, scoreMax: 12 },
      { id: "biomedical_4", label: "Biomedical — Set 4", duration: 60, scoreMax: 13 },
      { id: "clinical_1",   label: "Clinical — Set 1",   duration: 60, scoreMax: 12 },
      { id: "clinical_2",   label: "Clinical — Set 2",   duration: 60, scoreMax: 13 },
      { id: "clinical_3",   label: "Clinical — Set 3",   duration: 60, scoreMax: 12 },
      { id: "clinical_4",   label: "Clinical — Set 4",   duration: 60, scoreMax: 13 },
    ],
  },

  // Professional certifications
  cfa: {
    name: "CFA Level 1", slug: "cfa", color: "emerald", category: "professional", tier: "generic",
    description: "Chartered Financial Analyst — Level 1 exam",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "coming_soon", available: false, mediaStatus: "text-only",
    scoreLabel: "%", icon: TrendingUp,
    sections: [
      { id: "cfa_am", label: "CFA Level 1 — Session 1", duration: 135, scoreMax: 90 },
      { id: "cfa_pm", label: "CFA Level 1 — Session 2", duration: 135, scoreMax: 90 },
    ],
  },
  sie: {
    name: "SIE", slug: "sie", color: "blue", category: "professional", tier: "generic",
    description: "FINRA Securities Industry Essentials exam",
    // Promoted to live 2026-07-19: calibration 21% flagged (healthy tier),
    // zero question reuse after recomposition, promotion E2E 75/75 → 100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: BarChart3,
    sections: [
      { id: "sie", label: "SIE", duration: 105, scoreMax: 100 },
    ],
  },
  pmp: {
    name: "PMP", slug: "pmp", color: "blue", category: "professional", tier: "generic",
    description: "Project Management Professional certification",
    // Promoted to live 2026-08-03: 43 definitional stems rebuilt situational,
    // 60 dedupe + 177 subtler-distractor rewrites; judge 46%->25% flagged
    // (easy 38%->29%); 0% near-dup; promotion E2E 100/100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: ClipboardList,
    sections: [
      { id: "pmp", label: "PMP", duration: 230, scoreMax: 100 },
    ],
  },
  comptia: {
    name: "CompTIA Security+", slug: "comptia", color: "red", category: "professional", tier: "generic",
    description: "CompTIA Security+ (SY0-701) — cybersecurity fundamentals certification",
    // Promoted to live 2026-07-19: catalog rebuilt as 10 scenario forms (900
    // unique blueprint-weighted questions, cross-vendor verified); calibration
    // judge re-scored 88%→25% flagged; promotion E2E 90/90 → 100.
    v1ScoreMax: 900, v2ScoreMax: 900, status: "live", available: true, mediaStatus: "complete",
    scoreLabel: "/900", icon: Monitor,
    sections: [
      { id: "comptia", label: "CompTIA Security+", duration: 90, scoreMax: 900 },
    ],
  },
  asvab: {
    name: "ASVAB", slug: "asvab", color: "slate", category: "professional", tier: "generic",
    description: "Armed Services Vocational Aptitude Battery",
    // Promoted to live 2026-07-19: calibration 25% flagged, zero reuse after
    // recomposition, promotion E2E 90/90 → 99th percentile (scale max).
    v1ScoreMax: 99, v2ScoreMax: 99, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "th percentile", icon: Medal,
    sections: [
      { id: "ar",  label: "Arithmetic Reasoning",    duration: 36, scoreMax: 30 },
      { id: "wk",  label: "Word Knowledge",           duration: 9,  scoreMax: 35 },
      { id: "pc",  label: "Paragraph Comprehension",  duration: 27, scoreMax: 15 },
      { id: "mk",  label: "Mathematics Knowledge",    duration: 24, scoreMax: 25 },
      { id: "gs",  label: "General Science",          duration: 11, scoreMax: 25 },
    ],
  },
  servsafe: {
    name: "ServSafe", slug: "servsafe", color: "orange", category: "professional", tier: "generic",
    description: "ServSafe Manager food safety certification",
    // Promoted to live 2026-07-24: blueprint rebuild + strict-gate requality
    // (61 weak items replaced) + dedupe to 0% near-dup (47 replaced); judge
    // 29% flagged (corrected counter); promotion E2E 100/100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Utensils,
    sections: [
      { id: "servsafe", label: "ServSafe Manager", duration: 90, scoreMax: 100 },
    ],
  },

  // English proficiency
  "cambridge-c1": {
    name: "Cambridge C1", slug: "cambridge-c1", color: "blue", category: "english", tier: "generic",
    description: "Cambridge C1 Advanced (CAE) English certification",
    // Promoted to live 2026-07-19: parts 5-8 texts generated + route/scorer
    // grouping unified; 62 ambiguous cloze replaced; band scales fixed
    // (perfect run now 9.0/9.0, was capped 7.5/5.5); 20 unanswerable
    // container items removed; judge 17% flagged / 88% realistic (best on
    // platform); promotion E2E: reading 33/33 + listening 22/22 → band 9.0.
    v1ScoreMax: 210, v2ScoreMax: 210, status: "live", available: true, mediaStatus: "complete",
    scoreLabel: "/210", icon: GraduationCap,
    sections: [
      { id: "reading", label: "Reading & Use of English", duration: 90, scoreMax: 70 },
      { id: "writing", label: "Writing",                  duration: 90, scoreMax: 70 },
      { id: "listening", label: "Listening",              duration: 40, scoreMax: 35 },
      { id: "speaking",  label: "Speaking",               duration: 15, scoreMax: 35 },
    ],
  },
  "duolingo-english": {
    name: "Duolingo English Test", slug: "duolingo-english", color: "green", category: "english", tier: "generic",
    description: "Duolingo English Test — computer-adaptive online exam",
    v1ScoreMax: 160, v2ScoreMax: 160, status: "beta", available: true, mediaStatus: "text-only",
    scoreLabel: "/160", icon: Bird,
    sections: [
      { id: "duolingo", label: "Duolingo English", duration: 60, scoreMax: 160 },
    ],
  },
  oet: {
    name: "OET", slug: "oet", color: "cyan", category: "english", tier: "generic",
    description: "Occupational English Test — for healthcare professionals",
    v1ScoreMax: 500, v2ScoreMax: 500, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/500", icon: HeartPulse,
    sections: [
      { id: "listening", label: "Listening", duration: 50, scoreMax: 500 },
      { id: "reading",   label: "Reading",   duration: 60, scoreMax: 500 },
      { id: "writing",   label: "Writing",   duration: 45, scoreMax: 500 },
      { id: "speaking",  label: "Speaking",  duration: 20, scoreMax: 500 },
    ],
  },
  pte: {
    name: "PTE Academic", slug: "pte", color: "indigo", category: "english", tier: "generic",
    description: "Pearson Test of English Academic",
    v1ScoreMax: 90, v2ScoreMax: 90, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/90", icon: Laptop,
    sections: [
      { id: "speaking",  label: "Speaking",  duration: 40, scoreMax: 90 },
      { id: "writing",   label: "Writing",   duration: 30, scoreMax: 90 },
      { id: "reading",   label: "Reading",   duration: 30, scoreMax: 90 },
      { id: "listening", label: "Listening", duration: 45, scoreMax: 90 },
    ],
  },

  // Licensing & vocational
  cdl: {
    name: "CDL", slug: "cdl", color: "amber", category: "licensing", tier: "generic",
    description: "Commercial Driver's License general knowledge exam",
    // Promoted to live 2026-07-24: requality (115 replaced) + dedupe to 0%
    // near-dup (40 replaced); judge 29% flagged; promotion E2E 100/100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Truck,
    sections: [
      { id: "cdl", label: "CDL General Knowledge", duration: 90, scoreMax: 100 },
    ],
  },
  "real-estate": {
    name: "Real Estate License", slug: "real-estate", color: "emerald", category: "licensing", tier: "generic",
    description: "State real estate salesperson license exam",
    // Promoted to live 2026-07-19: calibration 21% flagged, catalog recomposed
    // 171→10 honest forms (zero reuse), promotion E2E 120/120 → 100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Home,
    sections: [
      { id: "real-estate", label: "Real Estate License", duration: 150, scoreMax: 100 },
    ],
  },
  "insurance-license": {
    name: "Insurance L&H License", slug: "insurance-license", color: "sky", category: "licensing", tier: "generic",
    description: "Life & Health insurance producer license exam",
    // Promoted to live 2026-07-19: calibration 21% flagged, recomposed to 9
    // honest forms (zero reuse), promotion E2E 150/150 → 100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Shield,
    sections: [
      { id: "insurance", label: "Life & Health Insurance", duration: 150, scoreMax: 100 },
    ],
  },
  cosmetology: {
    name: "Cosmetology License", slug: "cosmetology", color: "pink", category: "licensing", tier: "generic",
    description: "State cosmetology license exam",
    // Promoted to live 2026-07-24: requality resweep + dedupe to 0% near-dup
    // (50 replaced); judge 29% flagged; promotion E2E 100/100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Scissors,
    sections: [
      { id: "cosmetology", label: "Cosmetology", duration: 120, scoreMax: 100 },
    ],
  },
  "faa-part-107": {
    name: "FAA Part 107", slug: "faa-part-107", color: "sky", category: "licensing", tier: "generic",
    description: "FAA Part 107 Commercial Drone Pilot certification",
    // Promoted to live 2026-08-06: bank rebuilt on the FAA's public-domain
    // UAG sample exam (30 official items seeded, authentic 3-option format,
    // 5 full-length forms); judge 50%->29% flagged; near-dup 1.0%;
    // promotion E2E 100/100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "complete",
    scoreLabel: "%", icon: Plane,
    sections: [
      { id: "faa", label: "FAA Part 107", duration: 120, scoreMax: 100 },
    ],
  },
  cna: {
    name: "CNA", slug: "cna", color: "teal", category: "licensing", tier: "generic",
    description: "Certified Nursing Assistant certification exam",
    // Promoted to live 2026-07-19: calibration 21% flagged (one of the
    // healthiest betas), zero reuse, promotion E2E 70/70 → 100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: HandHeart,
    sections: [
      { id: "cna", label: "CNA Knowledge Test", duration: 90, scoreMax: 100 },
    ],
  },
  nremt: {
    name: "NREMT", slug: "nremt", color: "red", category: "licensing", tier: "generic",
    description: "National Registry of Emergency Medical Technicians exam",
    // Promoted to live 2026-07-19: calibration 29% flagged, zero reuse after
    // recomposition, promotion E2E 110/110 → 100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Ambulance,
    sections: [
      { id: "nremt", label: "NREMT EMT", duration: 120, scoreMax: 100 },
    ],
  },
  "nasm-cpt": {
    name: "NASM CPT", slug: "nasm-cpt", color: "orange", category: "professional", tier: "generic",
    description: "NASM Certified Personal Trainer exam",
    // Promoted to live 2026-07-24: requality (88 replaced) + dedupe to 0%
    // near-dup (40 replaced); judge 29% flagged; promotion E2E 100/100.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "text-only",
    scoreLabel: "%", icon: Dumbbell,
    sections: [
      { id: "nasm", label: "NASM CPT", duration: 120, scoreMax: 100 },
    ],
  },

  // Foreign languages
  hsk: {
    name: "HSK 4", slug: "hsk", color: "red", category: "language", tier: "generic",
    description: "Chinese Proficiency Test (Hanyu Shuiping Kaoshi) Level 4",
    v1ScoreMax: 300, v2ScoreMax: 300, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/300", icon: Languages,
    sections: [
      { id: "listening", label: "听力 Listening", duration: 30, scoreMax: 100 },
      { id: "reading",   label: "阅读 Reading",   duration: 40, scoreMax: 100 },
      { id: "writing",   label: "书写 Writing",   duration: 25, scoreMax: 100 },
    ],
  },
  jlpt: {
    name: "JLPT N2", slug: "jlpt", color: "red", category: "language", tier: "generic",
    description: "Japanese-Language Proficiency Test — Level N2",
    v1ScoreMax: 180, v2ScoreMax: 180, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/180", icon: Languages,
    sections: [
      { id: "vocab_grammar", label: "言語知識 Vocabulary & Grammar", duration: 60, scoreMax: 60 },
      { id: "reading",       label: "読解 Reading",                  duration: 45, scoreMax: 60 },
      { id: "listening",     label: "聴解 Listening",                duration: 50, scoreMax: 60 },
    ],
  },
  topik: {
    name: "TOPIK II", slug: "topik", color: "blue", category: "language", tier: "generic",
    description: "Test of Proficiency in Korean — Level II",
    v1ScoreMax: 300, v2ScoreMax: 300, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/300", icon: Languages,
    sections: [
      { id: "listening", label: "Listening", duration: 60, scoreMax: 100 },
      { id: "reading",   label: "Reading",   duration: 70, scoreMax: 100 },
      { id: "writing",   label: "Writing",   duration: 50, scoreMax: 100 },
    ],
  },
  dele: {
    name: "DELE B2", slug: "dele", color: "amber", category: "language", tier: "generic",
    description: "Diplomas of Spanish as a Foreign Language — Level B2",
    // Promoted to live 2026-07-19: rebuilt 10 forms (500 verified items, 250
    // TTS clips), judge 83%→29% flagged / 92% realistic after four gate-driven
    // iterations; promotion E2E 54q → 100 incl. AI-scored writing path.
    v1ScoreMax: 100, v2ScoreMax: 100, status: "live", available: true, mediaStatus: "complete",
    scoreLabel: "%", icon: Languages,
    sections: [
      { id: "reading",   label: "Comprensión de lectura",   duration: 70, scoreMax: 25 },
      { id: "listening", label: "Comprensión auditiva",     duration: 40, scoreMax: 25 },
      { id: "writing",   label: "Expresión e interacción escritas", duration: 80, scoreMax: 25 },
      { id: "speaking",  label: "Expresión e interacción orales", duration: 15, scoreMax: 25},
    ],
  },
  delf: {
    name: "DELF B2", slug: "delf", color: "blue", category: "language", tier: "generic",
    description: "Diplôme d'études en langue française — Niveau B2",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/100", icon: Languages,
    sections: [
      { id: "reading",   label: "Compréhension écrite",     duration: 60, scoreMax: 25 },
      { id: "listening", label: "Compréhension de l'oral",  duration: 30, scoreMax: 25 },
      { id: "writing",   label: "Production écrite",        duration: 60, scoreMax: 25 },
      { id: "speaking",  label: "Production orale",         duration: 20, scoreMax: 25},
    ],
  },
  "goethe-c1": {
    name: "Goethe-Zertifikat C1", slug: "goethe-c1", color: "yellow", category: "language", tier: "generic",
    description: "Goethe-Zertifikat C1 — advanced German language exam",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "beta", available: true, mediaStatus: "complete",
    scoreLabel: "/100", icon: Languages,
    sections: [
      { id: "reading",      label: "Leseverstehen",          duration: 70, scoreMax: 25 },
      { id: "language_use", label: "Sprachbausteine",        duration: 30, scoreMax: 25 },
      { id: "listening",    label: "Hörverstehen",           duration: 40, scoreMax: 25 },
      { id: "writing",      label: "Schriftlicher Ausdruck", duration: 80, scoreMax: 25 },
      { id: "speaking",     label: "Mündlicher Ausdruck",    duration: 15, scoreMax: 25},
    ],
  },
  fsp: {
    name: "FSP (Fachsprachprüfung)", slug: "fsp", color: "yellow", category: "language", tier: "generic",
    description: "German medical professional language exam for foreign doctors",
    v1ScoreMax: 100, v2ScoreMax: 100, status: "coming_soon", available: false, mediaStatus: "complete",
    scoreLabel: "/100", icon: Stethoscope,
    sections: [
      { id: "anamnese",      label: "Anamnese",            duration: 20, scoreMax: 33 },
      { id: "arztbrief",     label: "Arztbrief",           duration: 20, scoreMax: 33 },
      { id: "arztgespraech", label: "Arzt-Arzt-Gespräch", duration: 20, scoreMax: 34 },
    ],
  },
};

export function getExamConfig(slug: string): ExamConfig | null {
  return EXAM_CONFIGS[slug as ExamType] ?? null;
}

export function getAllExams(): ExamConfig[] {
  return Object.values(EXAM_CONFIGS);
}

export function getExamsByCategory(category: ExamCategory): ExamConfig[] {
  return Object.values(EXAM_CONFIGS).filter(e => e.category === category);
}

export function getGenericExams(): ExamConfig[] {
  return Object.values(EXAM_CONFIGS).filter(e => e.tier === "generic");
}

export const ALL_EXAM_SLUGS: ExamType[] = Object.keys(EXAM_CONFIGS) as ExamType[];
